import React from "react";
import {observer} from "mobx-react-lite";
import {Alert} from "@mui/material";
import type {Initiative} from "../../../ApiTypes";
import {DoiStatus, Priority} from "../../../ApiTypes";
import {useSitStores} from "~/stores";
import dayjs from "dayjs";
import {sitFieldLabel, stageGateLabel} from "../../../sitLabels";
import {SitField, SitRead, SitTrafficDot} from "../../../components/design/SitDesignAtoms";
import i18n from "../../../../i18n/i18n";

export const InitiativePlaningFrom: React.FC = observer(() => {
    const {initiativeInstanceStore: s} = useSitStores();
    const dis = s.disabled;
    const fc = s.f_c[0];

    // Gate-agenda rows (stages 2-4) + a helper to spread their due dates evenly
    // between the start date and the "EBIT effective" (DOI 5) date.
    const agenda = s.doi_c.filter(sc => [2, 3, 4].indexOf(sc.instance.doi_stage ?? 0) > -1);
    const spreadEqually = () => {
        const span = s.doi5Date.diff(s.startDate);
        agenda.forEach((sc) => {
            const frac = ((sc.instance.doi_stage ?? 0) - 1) / 4; // N2 → 1/4, N3 → 1/2, N1 → 3/4
            sc.dueDate = s.startDate.add(span * frac, 'millisecond');
        });
    };

    return (
        <>
            <div className="sit-grid">
                <SitField label={sitFieldLabel('stageGate')}>
                    <select value={s.doiStatus} disabled={dis}
                            onChange={e => s.doiStatus = e.target.value}>
                        {DoiStatus.map((value, index) =>
                            <option key={index} value={value}>{stageGateLabel(index + 1)}</option>)}
                    </select>
                </SitField>
                <SitField label="Start date">
                    <input type="date" disabled={dis} value={s.startDate.format('YYYY-MM-DD')}
                           onChange={e => {
                               if (e.target.value) s.startDate = dayjs(e.target.value);
                           }}/>
                </SitField>
                <SitField label={`"${i18n.t('stageGate.5', {ns: 'sit'})}" project date`}
                          help="Default: current month + 12 months.">
                    <input type="date" disabled={dis}
                           value={s.doi5Date.format('YYYY-MM-DD')}
                           onChange={e => {
                               if (e.target.value) s.doi5Date = dayjs(e.target.value);
                           }}/>
                </SitField>
                <SitField label="Affected period" units="months">
                    <input type="number" disabled={dis} value={fc.instance.affected_months ?? 0}
                           onChange={e => fc.affectedMonths = parseInt(e.target.value) || 0}/>
                </SitField>
                <SitField label="Priority">
                    <select value={s.priority} disabled={dis}
                            onChange={e => s.priority = e.target.value}>
                        {Priority.map((value, index) => <option key={index} value={value}>{value}</option>)}
                    </select>
                </SitField>
                <SitRead label="Traffic light"
                         value={<SitTrafficDot initiative={s.instance as Initiative} withLabel/>}/>
                <SitRead label="Elapsed since start"
                         value={`${dayjs().diff(s.startDate, 'month')} month(s)`}/>
            </div>

            {s.doiStatus === DoiStatus[4] &&
                <Alert severity="info" color="success" className="mt-2">
                    Setting stage to "{stageGateLabel(5)}" requires approval.
                </Alert>}

            {/* Supply-squad gate agenda: planned vs actual dates + presentation log */}
            <div className="sit-agenda" style={{marginTop: 16}}>
                <div className="sit-agenda-head" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <strong>{i18n.t('timelineTitle', {ns: 'sit'})}</strong>
                    <span style={{flex: 1}}/>
                    <button type="button" className="ac-btn ac-btn--sm" disabled={dis} onClick={spreadEqually}>
                        Spread equally
                    </button>
                </div>
                {agenda.map((sc, index) => {
                    const mandatory = [1, 2].indexOf(s.instance.complexity_tier ?? 0) > -1;
                    const reached = (s.instance.doi_status ?? 0) >= sc.instance.doi_stage;
                    const showWarning = mandatory && reached && !sc.presented;
                    // Status dot: done (actual set) / overdue (past due, not done) / on-track.
                    const hue = sc.actualDate ? 'good' : (sc.dueDate.isBefore(dayjs(), 'day') ? 'bad' : 'muted');
                    return (
                        <div key={index} style={{marginBottom: 10}}>
                            <div className="sit-grid">
                                <SitField
                                    label={stageGateLabel(sc.instance.doi_stage) + ' · ' + sitFieldLabel('planned')}>
                                    <input type="date" disabled={dis} value={sc.dueDate.format('YYYY-MM-DD')}
                                           onChange={e => {
                                               if (e.target.value) sc.dueDate = dayjs(e.target.value);
                                           }}/>
                                </SitField>
                                <SitField label={sitFieldLabel('actual')}>
                                    <input type="date" disabled={dis}
                                           value={sc.actualDate ? sc.actualDate.format('YYYY-MM-DD') : ''}
                                           onChange={e => sc.actualDate = e.target.value ? dayjs(e.target.value) : null}/>
                                </SitField>
                                <label className="sitf-inline">
                                    <input type="checkbox" checked={sc.presented} disabled={dis}
                                           onChange={e => sc.presented = e.target.checked}/>
                                    <span className="sitf-label">{sitFieldLabel('presented')}</span>
                                    {sc.presented && sc.presentedDate &&
                                        <span className="sit-check-date">{sc.presentedDate.format('YYYY-MM-DD')}</span>}
                                </label>
                                <div className="sitf-inline">
                                    <span className="sitf-label">Status</span>
                                    <span className={'traffic hue-' + hue}>
                                        <span className="traffic-dot"/>
                                    </span>
                                </div>
                            </div>
                            {showWarning && <Alert severity="warning"
                                                   className="mt-1">{i18n.t('agendaWarning', {ns: 'sit'})}</Alert>}
                        </div>
                    );
                })}
            </div>
        </>
    );
});
